import { PortalChrome, HealthPill, StatusPill, MOCK, useRole } from "./_shared";
import { ArrowRight, AlertTriangle, TrendingUp, Package, Clock, Sparkles, ChevronRight, CheckCircle2 } from "lucide-react";

// Option 2 — "Bento Dashboard" (Faire supplier + Notion home + Ramp inspired)
// NO TABS. Everything is modular cards of varying sizes on a single scroll page.
// The mental model is "operating dashboard" not "app with tabs".

export default function Option2_Bento() {
  const [role, setRole] = useRole();
  const m = MOCK[role];
  const isSeller = role === "seller";

  return (
    <PortalChrome role={role} setRole={setRole} optionLabel="Option 2 · Bento dashboard" optionNumber="2">
      <div className="border-b border-zinc-200 bg-white px-6 lg:px-8 py-5">
        <h1 className="text-2xl font-semibold tracking-tight">Dropship</h1>
        <p className="text-sm text-zinc-500 mt-0.5">
          {isSeller ? "Mirror your catalog to buyer channels. Verqo handles routing, invoices, claims." : "Add supplier products to your store. Verqo handles pricing, sync, invoices."}
        </p>
      </div>

      <main className="flex-1 overflow-y-auto p-6 lg:p-8 bg-zinc-50">
        {/* Row 1 — Hero: next action + KPIs */}
        <div className="grid grid-cols-12 gap-4 mb-4">
          {/* Next action — 5 cols */}
          <div className="col-span-12 lg:col-span-5 rounded-2xl bg-zinc-900 text-white p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl"></div>
            <div className="relative">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-amber-300 mb-3">
                <Sparkles className="w-3 h-3" /> Next up
              </div>
              <div className="text-2xl font-semibold tracking-tight mb-2">
                {isSeller ? "Add tracking to DS-21041" : "Sign amendment from Sunbelt Snack"}
              </div>
              <p className="text-sm text-zinc-300 mb-4">
                {isSeller ? "Daisy & Co Boutique · Anna L. · SLA 2h left" : "Sunbelt Snack proposed a handling fee change · you have 24h to accept"}
              </p>
              <button className="text-xs font-semibold bg-white text-zinc-900 px-3 py-1.5 rounded-md inline-flex items-center gap-1.5">
                Open <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* KPI tiles — 7 cols in a 2x2 grid */}
          <div className="col-span-12 lg:col-span-7 grid grid-cols-2 gap-3">
            {m.kpis.map(k => (
              <div key={k.label} className="rounded-2xl bg-white border border-zinc-200/80 p-4">
                <div className="text-[11px] uppercase tracking-widest text-zinc-500">{k.label}</div>
                <div className={`text-3xl font-semibold tracking-tight mt-1 ${k.accent === "warn" ? "text-amber-600" : "text-zinc-900"}`}>{k.value}</div>
                {k.hint && <div className="text-xs text-zinc-500 mt-1">{k.hint}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Row 2 — Relationships table (large) + activity feed (small) */}
        <div className="grid grid-cols-12 gap-4 mb-4">
          {/* Relationships — 8 cols */}
          <div className="col-span-12 lg:col-span-8 rounded-2xl bg-white border border-zinc-200/80 overflow-hidden">
            <div className="px-5 py-4 flex items-center justify-between border-b border-zinc-100">
              <div>
                <div className="text-sm font-semibold">{m.partnersLabel}</div>
                <div className="text-xs text-zinc-500">Click any row for full relationship detail</div>
              </div>
              <button className="text-xs font-medium text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
                View all <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-400">
                <tr className="border-b border-zinc-100">
                  <th className="text-left font-medium px-5 py-2">{m.partnerLabel}</th>
                  {isSeller && <th className="text-left font-medium px-5 py-2">Channel</th>}
                  <th className="text-right font-medium px-5 py-2">Orders 30d</th>
                  <th className="text-right font-medium px-5 py-2">GMV 30d</th>
                  <th className="text-left font-medium px-5 py-2">Sync</th>
                  <th className="text-left font-medium px-5 py-2">MAP</th>
                  <th className="px-5 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {m.partners.map(p => (
                  <tr key={p.id} className="border-b border-zinc-50 last:border-0 hover:bg-zinc-50/60 cursor-pointer">
                    <td className="px-5 py-3 font-medium">{p.name}</td>
                    {isSeller && <td className="px-5 py-3 text-zinc-600">{p.channel}</td>}
                    <td className="px-5 py-3 text-right tabular-nums">{p.orders30d}</td>
                    <td className="px-5 py-3 text-right tabular-nums">${p.gmv30d.toLocaleString()}</td>
                    <td className="px-5 py-3"><HealthPill kind={p.health}>{p.sync}</HealthPill></td>
                    <td className="px-5 py-3">
                      {p.mapOk ? <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Clean</span> : <span className="text-[11px] text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Flag</span>}
                    </td>
                    <td className="px-5 py-3 text-right"><ChevronRight className="w-3.5 h-3.5 text-zinc-300 inline" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Activity feed — 4 cols */}
          <div className="col-span-12 lg:col-span-4 rounded-2xl bg-white border border-zinc-200/80 p-5">
            <div className="text-sm font-semibold mb-4">Recent activity</div>
            <div className="space-y-4 relative before:content-[''] before:absolute before:top-2 before:bottom-2 before:left-[7px] before:w-px before:bg-zinc-100">
              {[
                { color: "bg-emerald-500", title: "Invoice $211 · debited", desc: "Northside Pharmacy · 12m ago" },
                { color: "bg-blue-500",    title: "Tracking auto-pushed",    desc: "DS-21040 · 1h ago" },
                { color: "bg-amber-500",   title: "MAP violation flagged",   desc: "Urban Tots · 2h ago" },
                { color: "bg-zinc-400",    title: "New order",               desc: "DS-21041 · 3h ago" },
                { color: "bg-red-500",     title: "Claim opened",            desc: "Coastal Mercantile · 1d ago" },
              ].map((a, i) => (
                <div key={i} className="flex items-start gap-3 relative">
                  <div className={`w-3.5 h-3.5 rounded-full ${a.color} ring-4 ring-white flex-shrink-0`}></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium">{a.title}</div>
                    <div className="text-[11px] text-zinc-500">{a.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 3 — Orders + Catalog snapshot side by side */}
        <div className="grid grid-cols-12 gap-4 mb-4">
          <div className="col-span-12 lg:col-span-7 rounded-2xl bg-white border border-zinc-200/80 overflow-hidden">
            <div className="px-5 py-4 flex items-center justify-between border-b border-zinc-100">
              <div>
                <div className="text-sm font-semibold">Order pipeline</div>
                <div className="text-xs text-zinc-500">5 orders · last 7 days</div>
              </div>
              <button className="text-xs font-medium text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
                All orders <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            {m.orders.slice(0, 4).map(o => (
              <div key={o.id} className="px-5 py-3 border-b border-zinc-50 last:border-0 flex items-center gap-3 hover:bg-zinc-50/60">
                <span className="font-mono text-[10px] text-zinc-500 w-20">{o.id}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{o.customer}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{o.partner} · {o.note}</div>
                </div>
                <span className="tabular-nums text-sm font-medium">${o.total.toFixed(2)}</span>
                <StatusPill status={o.status} />
              </div>
            ))}
          </div>

          <div className="col-span-12 lg:col-span-5 rounded-2xl bg-white border border-zinc-200/80 p-5">
            <div className="text-sm font-semibold mb-3">{isSeller ? "Top mirrored SKUs" : "Highest-margin available"}</div>
            <div className="space-y-2">
              {m.catalog.slice(0, 4).map(p => (
                <div key={p.sku} className="flex items-center gap-3 py-2 border-b border-zinc-50 last:border-0">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-zinc-100 to-zinc-50 flex-shrink-0 flex items-center justify-center">
                    <Package className="w-3.5 h-3.5 text-zinc-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-[11px] text-zinc-500 font-mono">{p.sku}</div>
                  </div>
                  {isSeller
                    ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-zinc-100">{p.mirrored} buyers</span>
                    : <span className="text-[11px] font-semibold text-emerald-700">{Math.round(p.margin * 100)}%</span>}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 4 — Trends + growth (illustrative) */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-8 rounded-2xl bg-white border border-zinc-200/80 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm font-semibold">GMV · last 30 days</div>
                <div className="text-xs text-zinc-500">+18% vs prior period</div>
              </div>
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            </div>
            {/* fake sparkline bars */}
            <div className="flex items-end gap-1 h-24">
              {[40, 55, 42, 60, 48, 72, 58, 65, 70, 82, 75, 88, 78, 90, 82, 96, 88, 100, 94, 108, 100, 112, 105, 118, 110, 124, 118, 130, 125, 138].map((h, i) => (
                <div key={i} className="flex-1 rounded-sm" style={{ height: `${h * 0.65}%`, background: `linear-gradient(180deg, rgb(16 185 129 / ${0.4 + (i / 30) * 0.5}), rgb(16 185 129 / 0.15))` }}></div>
              ))}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-4 rounded-2xl bg-white border border-zinc-200/80 p-5">
            <div className="text-sm font-semibold mb-4">Open claims · 2</div>
            <div className="space-y-2">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold">Damaged on arrival</div>
                    <div className="text-[11px] text-zinc-600">Coastal Mercantile · 1d</div>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div className="flex items-start gap-2">
                  <Clock className="w-3.5 h-3.5 text-amber-600 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold">No carrier movement</div>
                    <div className="text-[11px] text-zinc-600">Urban Tots · 2d</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </PortalChrome>
  );
}
